// Thin wrapper around the OpenStreetMap Nominatim search API, scoped to the
// Philippines to match the original app's delivery area.
//
// Nominatim's public instance is free but **not unlimited**: its usage policy allows
// roughly one request per second and forbids bulk use. Everything here is therefore
// per-interaction — a debounced search as somebody types, and one reverse lookup when a
// pasted link resolves. Nothing loops, and nothing runs per row of a list.
/**
 * Los Baños and Bay, the same box Places is restricted to.
 *
 * The bounding rectangle of the two real PSGC boundaries the backend checks against,
 * measured from `backend/src/config/service-area.geo.json` and padded by about 550 m so a
 * shop whose recorded pin sits just over a surveyor's line does not vanish.
 *
 * The fallback has to answer the same question as Places or it is not a fallback — a
 * customer whose search was answered by Google yesterday and by Nominatim today would get
 * results from Calamba on one of those days and not the other, with nothing to explain it.
 *
 * Nominatim's order is `minLng,minLat,maxLng,maxLat`, and `bounded=1` is what makes it a
 * restriction rather than a hint.
 */
const VIEWBOX = '121.16631,14.07732,121.30628,14.20315'

export async function searchLocations(query) {
  const url = 'https://nominatim.openstreetmap.org/search'
    + `?format=json&countrycodes=ph&limit=5&viewbox=${VIEWBOX}&bounded=1`
    + `&q=${encodeURIComponent(query)}`

  const res = await fetch(url, { headers: { 'Accept-Language': 'en' } })
  const data = await res.json()
  if (!Array.isArray(data)) return []
  return data.map((d) => ({
    lat: parseFloat(d.lat),
    lng: parseFloat(d.lon),
    label: d.display_name,
  }))
}

/**
 * Coordinates back into an address.
 *
 * This exists because of what a rider is holding at the door. When a customer pastes a
 * Google Maps link instead of searching, the app had nothing to name the place with, so it
 * stored the coordinates *as the address* — the one real order in the database carries
 * `dropoffAddress: "14.17759, 121.24078"`. That string is then what the partner sees in
 * their console and what the rider gets on their phone: a pin, and no street to ask
 * anybody for.
 *
 * Looked up **once, when the link resolves**, rather than at display time. The address is
 * then stored on the order and is correct everywhere afterwards — three consoles reading
 * it never make a request, which is what keeps this inside Nominatim's usage policy.
 *
 * Returns null on any failure. A geocoder being down must never stop somebody ordering
 * food; the coordinates are still the authoritative location and the map still works.
 */
export async function reverseGeocode(lat, lng) {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null

  const url = `https://nominatim.openstreetmap.org/reverse?format=json&zoom=18&addressdetails=1&lat=${lat}&lon=${lng}`

  try {
    const res = await fetch(url, { headers: { 'Accept-Language': 'en' } })
    if (!res.ok) return null

    const data = await res.json()

    return typeof data?.display_name === 'string' && data.display_name.trim()
      ? data.display_name
      : null
  } catch {
    return null
  }
}
